import type { ReactNode } from 'react';
import type { TranscriptItem } from '@/data/fixtures';
import type { PodcastTranscriptItem } from '@/lib/api-client';
import { LessonPanelHeader } from '../media/TextLessonFrame';
import { formatClock } from './PodcastPill';

/**
 * Normalize a backend `PodcastTranscriptItem` (start_time / end_time)
 * into the local UI `TranscriptItem` shape (startTime / endTime), so
 * the existing transcript renderer works against either source.
 */
export function toUiTranscript(
  items: PodcastTranscriptItem[]
): TranscriptItem[] {
  return items.map(item => ({
    id: item.id,
    startTime: item.start_time,
    endTime: item.end_time,
    timestamp: formatClock(item.start_time).padStart(5, '0'),
    text: item.text,
    speaker: item.speaker,
  }));
}

/**
 * Header, player and transcript in the Text tab's reading column. `top` sits
 * above the header (the way back to the list); the first child (the player)
 * under it; the second (the transcript) fills the rest of the panel and
 * scrolls on its own. The outer box does not scroll, and MediaViewer drops
 * its `p-4` for this tab so the transcript scrollbar lands flush against the
 * panel border.
 */
export function PodcastLayout({
  title,
  duration,
  meta = 'Podcast',
  top,
  children,
}: {
  title: string;
  duration: number;
  meta?: string;
  top?: ReactNode;
  children: [ReactNode, ReactNode];
}) {
  const minutes = duration > 0 ? Math.max(1, Math.round(duration / 60)) : null;
  return (
    <div className='flex h-full flex-col'>
      <div className='px-6 pt-8 sm:px-10'>
        <div className='mx-auto w-full max-w-[68ch]'>
          {top}
          <LessonPanelHeader
            title={title}
            meta={[meta, 'Two hosts', minutes && `${minutes} min`]}
            className='mb-6'
          />
          {children[0]}
        </div>
      </div>
      <div className='mt-8 flex min-h-0 flex-1 flex-col'>{children[1]}</div>
    </div>
  );
}
